import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { KeycloakService } from '@app/core/keycloak.service';
import { HelperService } from './helper.service';
import { APIRequestService } from './api/apirequest.service';
import { APIURLTypes } from '../enums/apiurltypes.enum';

/** Backend error-code: the user is not (or no longer) an active member of the request's organization. */
export const ORG_MEMBERSHIP_INACTIVE_CODE = 'ORGAD02';

export interface AvailableOrg {
  id: number;
  uuid: string;
  name: string;
  role: string;
}

export interface InactiveOrgState {
  /** Name of the organization the user can no longer use. */
  orgName: string;
  /** Organizations the user is still an active member of (empty: none left). */
  orgs: AvailableOrg[];
  /** True while the list of remaining organizations is loading. */
  loading: boolean;
  /** Id of the organization being switched to, while its profile loads. */
  switchingTo: number | null;
  error: string | null;
}

/**
 * Handles the active organization becoming inactive for the user while they are working in it (e.g. a superadmin
 * removed them from it). Opens the "organization inactive" dialog, from which the user switches to another active
 * organization or logs out.
 */
@Injectable({
  providedIn: 'root'
})
export class OrgAccessService {
  readonly state$ = new BehaviorSubject<InactiveOrgState | null>(null);

  constructor(
    private injector: Injector,
    private helperService: HelperService,
    private keycloakService: KeycloakService
  ) {}

  public isOrgAccessDenied(response: any): boolean {
    return response?.status === 403 && response?.error?.['error-code'] === ORG_MEMBERSHIP_INACTIVE_CODE;
  }

  /** Opens the dialog once, however many requests were rejected, and loads the organizations still available. */
  public showInactiveOrgDialog(): void {
    if (this.state$.value) {
      return;
    }
    const inactiveId = this.helperService.getCustomerId();
    const inactive = (this.helperService.customers.get() || [])
      .find(c => Number(c.customer_id) === Number(inactiveId));
    this.state$.next({
      orgName: inactive?.customer_name || inactive?.name || 'your current organization',
      orgs: [], loading: true, switchingTo: null, error: null
    });

    // An explicit empty x-org-id stops TokenInterceptor from sending the inactive organization; the backend then
    // answers with the user's remaining active organizations (403 when there are none).
    this.api().get('', null, this.profileUrl(), { 'x-org-id': '' }).subscribe(
      (response: any) => this.patch({
        loading: false,
        orgs: (response?.customers || [])
          .filter(c => Number(c.customer_id) !== Number(inactiveId))
          .map(c => ({ id: Number(c.customer_id), uuid: c.customer_uuid, name: c.customer_name || c.name, role: c.role }))
      }),
      () => this.patch({ loading: false, orgs: [] })
    );
  }

  /**
   * Loads the profile (permissions, roles, settings) in the context of `org`, stores it as the active
   * organization and reloads the app on that profile's landing page, so every page starts from the new profile.
   */
  public switchTo(org: AvailableOrg): void {
    const state = this.state$.value;
    if (!state || state.switchingTo) {
      return;
    }
    this.patch({ switchingTo: org.id, error: null });
    const headers: { [name: string]: string } = { 'x-org-id': String(org.id) };
    if (org.uuid) {
      headers['x-organization-id'] = org.uuid;
    }
    this.api().get('', null, this.profileUrl(), headers).subscribe(
      (response: any) => {
        this.helperService.clearOrgScopedState();
        this.helperService.activeCustomerId.set(org.id);
        this.helperService.applyProfileResponse(response);
        window.location.assign(this.helperService.landingRoute());
      },
      () => this.patch({ switchingTo: null, error: 'Could not switch organization. Please try again or log out.' })
    );
  }

  public logout(): void {
    this.keycloakService.logout();
  }

  private patch(changes: Partial<InactiveOrgState>): void {
    if (this.state$.value) {
      this.state$.next({ ...this.state$.value, ...changes });
    }
  }

  private profileUrl() {
    return this.helperService.urlFactory(APIURLTypes.IAM, 'api/user/keycloak-profile');
  }

  /** Resolved lazily: APIRequestService depends on HttpClient, whose interceptors depend on this service. */
  private api(): APIRequestService {
    return this.injector.get(APIRequestService);
  }
}
